import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PaintBrushIcon, ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface StyleOption {
  key: string;
  value: string;
  category: string;
}

interface StyleSelectorProps {
  selectedStyle: string;
  onStyleChange: (style: string) => void;
  disabled?: boolean;
}

const styleOptions: StyleOption[] = [
  { key: 'pixar', value: '皮克斯', category: 'animation' },
  { key: '3dcg', value: '3DCG', category: 'modern' },
  { key: 'ancientIllustration', value: '古风插画', category: 'traditional' },
  { key: '3dCartoon', value: '3D卡通', category: 'animation' },
  { key: 'japaneseAnime', value: '日本动漫', category: 'animation' },
  { key: 'ghibli', value: '吉卜力', category: 'animation' },
  { key: 'cgIllustration', value: 'CG插画', category: 'modern' },
  { key: 'realistic', value: '写实风格', category: 'modern' },
  { key: 'koreanWebtoon', value: '现代韩国网络漫画风格', category: 'animation' },
  { key: 'disney', value: '迪士尼', category: 'animation' },
  { key: 'dreamworks', value: '梦工厂', category: 'animation' },
  { key: 'cyberpunk', value: '赛博朋克', category: 'scifi' },
  { key: 'steampunk', value: '蒸汽朋克', category: 'scifi' },
  { key: 'watercolor', value: '水彩风格', category: 'painting' },
  { key: 'oilPainting', value: '油画风格', category: 'painting' },
  { key: 'sketch', value: '素描风格', category: 'painting' },
  { key: 'ukiyoe', value: '浮世绘', category: 'traditional' },
  { key: 'chinesePainting', value: '国画风格', category: 'traditional' },
  { key: 'popArt', value: '波普艺术', category: 'modern' },
  { key: 'minimalism', value: '极简主义', category: 'modern' },
  { key: 'graffiti', value: '涂鸦艺术', category: 'modern' },
  { key: 'lowPoly', value: '低多边形', category: 'modern' },
  { key: 'neon', value: '霓虹效果', category: 'effects' },
  { key: 'glass', value: '玻璃质感', category: 'effects' },
  { key: 'metal', value: '金属质感', category: 'effects' },
  { key: 'retro', value: '复古风格', category: 'traditional' },
  { key: 'gothic', value: '哥特风格', category: 'traditional' },
  { key: 'nordic', value: '北欧风格', category: 'modern' },
  { key: 'industrial', value: '工业风格', category: 'modern' },
  { key: 'stopMotion', value: '定格动画', category: 'animation' },
  { key: 'printmaking', value: '版画风格', category: 'painting' },
];


export default function StyleSelector({ selectedStyle, onStyleChange, disabled }: StyleSelectorProps) {
  const [showSelector, setShowSelector] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const selectorRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation(['script', 'common']);

  const getLabel = (key: string) => t(`styleSelector.styles.${key}.label`);
  const getDescription = (key: string) => t(`styleSelector.styles.${key}.description`);
  const getCategoryLabel = (cat: string) => t(`styleSelector.category.${cat}`);

  const filteredOptions = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return styleOptions.filter(style =>
      getLabel(style.key).toLowerCase().includes(term) ||
      getDescription(style.key).toLowerCase().includes(term)
    );
  }, [searchTerm, t]);

  const groupedStyles = useMemo(() => {
    return filteredOptions.reduce((acc, style) => {
      const catLabel = getCategoryLabel(style.category);
      if (!acc[catLabel]) acc[catLabel] = [];
      acc[catLabel].push(style);
      return acc;
    }, {} as Record<string, StyleOption[]>);
  }, [filteredOptions, t]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
        setShowSelector(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleStyleSelect = (style: string) => {
    onStyleChange(style);
    setShowSelector(false);
    setSearchTerm('');
  };

  const handleClearStyle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onStyleChange('');
    setSearchTerm('');
  };

  // Find the display label for the currently selected style
  const selectedLabel = selectedStyle
    ? styleOptions.find(s => s.value === selectedStyle)
      ? getLabel(styleOptions.find(s => s.value === selectedStyle)!.key)
      : selectedStyle
    : '';

  return (
    <div className="relative" ref={selectorRef}>
      <button
        onClick={() => setShowSelector(!showSelector)}
        disabled={disabled}
        className={`flex items-center gap-1.5 px-3 py-1.5 bg-bg-elevated hover:bg-bg-surface text-gray-300 text-[13px] rounded-lg border transition-colors disabled:opacity-50 ${selectedStyle ? 'border-purple-500/50 bg-purple-500/10' : 'border-border-subtle'
          }`}
      >
        <PaintBrushIcon className={`w-3.5 h-3.5 ${selectedStyle ? 'text-purple-400' : 'text-gray-400'}`} />
        <span className={`max-w-20 truncate ${selectedStyle ? 'text-purple-300' : 'text-gray-300'}`}>
          {selectedLabel || t('styleSelector.label')}
        </span>
        {selectedStyle ? (
          <button
            onClick={handleClearStyle}
            className="p-0.5 hover:bg-bg-elevated rounded transition-colors cursor-pointer"
            title={t('styleSelector.clearStyle')}
          >
            <XMarkIcon className="w-3 h-3 text-gray-400 hover:text-gray-300" />
          </button>
        ) : (
          <ChevronDownIcon className="w-3 h-3 text-gray-400" />
        )}
      </button>

      {showSelector && (
        <div className="absolute top-full left-0 mt-2 bg-bg-page border border-border-subtle rounded-lg shadow-lg z-[9999] w-80 max-h-96 overflow-hidden">
          <div className="p-3 border-b border-border-subtle">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('styleSelector.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-bg-elevated text-gray-300 text-sm rounded border border-border-subtle focus:border-purple-500 outline-none"
              />
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {Object.keys(groupedStyles).length === 0 ? (
              <div className="p-4 text-center text-gray-400 text-sm">{t('styleSelector.noMatch')}</div>
            ) : (
              Object.entries(groupedStyles).map(([category, styles], index) => (
                <div key={category}>
                  <div className={`px-3 py-2 bg-bg-elevated/30 text-xs font-medium text-gray-400 uppercase tracking-wide ${index > 0 ? 'border-t border-border-subtle' : ''}`}>
                    {category}
                  </div>
                  {styles.map(style => (
                    <button
                      key={style.value}
                      onClick={() => handleStyleSelect(style.value)}
                      className={`w-full px-3 py-3 text-left hover:bg-bg-elevated transition-colors cursor-pointer ${selectedStyle === style.value ? 'bg-bg-elevated/50' : ''}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-medium ${selectedStyle === style.value ? 'text-purple-400' : 'text-gray-300'}`}>
                            {getLabel(style.key)}
                          </div>
                          <div className="text-xs text-gray-400 mt-1" style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}>
                            {getDescription(style.key)}
                          </div>
                        </div>
                        {selectedStyle === style.value && (
                          <div className="ml-2 w-2 h-2 bg-purple-400 rounded-full flex-shrink-0 mt-1.5" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}